/**
 * Routing policy for user-attached files: disk, prompt, or both.
 *
 * Before sandbox uploads existed these were one decision — a file was "supported" if and
 * only if it could be base64'd into the prompt, so a 200 MB archive was simply rejected.
 * They are now separate questions:
 *
 * - **Disk**: can the bytes be written into the sandbox workspace? Type is irrelevant
 *   here; only size and emptiness matter.
 * - **Prompt**: should the bytes *also* be inlined into the model's context? That stays
 *   a narrow allowlist, because inlining costs context and only some types are useful
 *   inline at all.
 *
 * A small screenshot does both: the model sees the pixels and the agent gets a real path
 * it can hand to the issue-creation flow. A large archive goes to disk only.
 */

/** Largest file we will write into the sandbox. */
export const MAX_SANDBOX_UPLOAD_BYTES = 100 * 1024 * 1024
export const MAX_SANDBOX_UPLOAD_LABEL = '100 MB'

/** Files written to the sandbox in a single turn. */
export const MAX_SANDBOX_UPLOADS_PER_TURN = 20

/** Total bytes written to the sandbox in a single turn. */
export const MAX_SANDBOX_UPLOAD_TOTAL_BYTES = 250 * 1024 * 1024
export const MAX_SANDBOX_UPLOAD_TOTAL_LABEL = '250 MB'

/**
 * Largest image or PDF we inline into the prompt. Above this the file is still uploaded,
 * but the model gets the path only — a 30 MB image costs far more context than it is
 * worth and risks the turn being rejected outright.
 */
export const MAX_INLINE_BINARY_BYTES = 10 * 1024 * 1024

/**
 * What the file is, for routing purposes.
 *
 * `other` is everything the prompt cannot usefully take inline — archives, spreadsheets,
 * binaries, datasets. Before sandbox uploads these were rejected outright; they are now
 * the main thing this feature exists to support.
 */
export type UploadFileKind = 'image' | 'pdf' | 'text' | 'other'

export interface UploadRoute {
  /** Write the bytes into the sandbox workspace. */
  toDisk: boolean
  /** Also inline the bytes into the prompt as an embedded resource. */
  toPrompt: boolean
}

export interface RouteInput {
  size: number
  kind: UploadFileKind
  /**
   * Whether a sandbox write is possible at all for this surface. When false the policy
   * collapses to the pre-existing inline-only behaviour, which is correct for surfaces
   * with no AHP relay (plain Copilot Chat) — there is no sandbox to write to.
   */
  sandboxAvailable: boolean
}

export function routeUploadedFile({size, kind, sandboxAvailable}: RouteInput): UploadRoute {
  if (!sandboxAvailable) {
    // Preserve the legacy contract exactly. Images were never size-capped on this path
    // (the 32 MB check sat only in the document branch), so capping them here would be a
    // silent regression for surfaces that have no sandbox to fall back on.
    return {toDisk: false, toPrompt: kind !== 'other'}
  }

  // Text goes to disk only. Images and PDFs are inlined as well as written, because a
  // resource attachment carries no bytes into the prompt: the model cannot see a picture
  // it has only been handed a path to, and reading one back does not put it in vision
  // context. The duplication buys vision for the model and a usable path for tooling.
  const inlineable = kind === 'image' || kind === 'pdf'
  return {toDisk: true, toPrompt: inlineable && size <= MAX_INLINE_BINARY_BYTES}
}
